import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';

/**
 * WebSocket service for real-time synthetic test execution updates.
 * Uses SockJS + STOMP protocol for reliable WebSocket communication.
 * Falls back to HTTP polling when WebSocket is not available.
 */
class SyntheticsWebSocketService {
  constructor() {
    this.stompClient = null;
    this.connected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000;
    this.listeners = new Map();
    this.connectionListeners = [];
    this.subscriptions = new Map();

    // Polling fallback
    this.pollingEnabled = false;
    this.pollingInterval = null;
    this.pollingDelay = 5000;
    this.lastEventTimestamp = null;
  }

  /**
   * Get the WebSocket URL based on current environment.
   */
  getWebSocketUrl() {
    const protocol = window.location.protocol;
    const host = window.location.host;
    return `${protocol}//${host}/ws`;
  }

  /**
   * Connect to the WebSocket server using SockJS + STOMP.
   */
  connect() {
    if (this.connected || (this.stompClient && this.stompClient.active)) {
      console.log('[WebSocket] Already connected or connecting');
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = this.getWebSocketUrl();
        console.log('[WebSocket] Connecting to:', wsUrl);

        this.stompClient = new Client({
          webSocketFactory: () => new SockJS(wsUrl),
          reconnectDelay: this.reconnectDelay,
          heartbeatIncoming: 10000,
          heartbeatOutgoing: 10000,
          debug: (str) => {
            // Uncomment for debugging:
            // console.log('[STOMP]', str);
          },
          onConnect: () => {
            console.log('[WebSocket] STOMP connected');
            this.connected = true;
            this.reconnectAttempts = 0;
            this.pollingEnabled = false;
            this.stopPolling();

            // Resubscribe to all topics
            this.listeners.forEach((callbacks, topic) => {
              this.subscribeToTopic(topic);
            });

            this.notifyConnectionListeners(true);
            resolve();
          },
          onDisconnect: () => {
            console.log('[WebSocket] STOMP disconnected');
            this.connected = false;
            this.subscriptions.clear();
            this.notifyConnectionListeners(false);
          },
          onStompError: (frame) => {
            console.error('[WebSocket] STOMP error:', frame.headers['message']);
            console.error('[WebSocket] Details:', frame.body);
          },
          onWebSocketError: (event) => {
            console.error('[WebSocket] WebSocket error:', event);
          },
          onWebSocketClose: (event) => {
            console.log('[WebSocket] WebSocket closed:', event.code, event.reason);
            this.connected = false;
            this.subscriptions.clear();
            this.notifyConnectionListeners(false);

            if (event.code !== 1000) {
              this.handleReconnect();
            }
          }
        });

        this.stompClient.activate();

      } catch (error) {
        console.error('[WebSocket] Failed to create connection:', error);
        this.enablePollingFallback();
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WebSocket server.
   */
  disconnect() {
    this.stopPolling();

    if (this.stompClient) {
      try {
        this.stompClient.deactivate();
      } catch (e) {
        console.warn('[WebSocket] Error deactivating client:', e);
      }

      this.stompClient = null;
      this.connected = false;
      this.subscriptions.clear();
      this.notifyConnectionListeners(false);
      console.log('[WebSocket] Disconnected');
    }
  }

  /**
   * Handle automatic reconnection.
   */
  handleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('[WebSocket] Max reconnect attempts reached, enabling polling fallback');
      this.enablePollingFallback();
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.min(this.reconnectAttempts, 3);
    console.log(`[WebSocket] Attempting reconnect in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect().catch(() => {
        // Error handled in connect
      });
    }, delay);
  }

  /**
   * Subscribe to a STOMP topic.
   */
  subscribeToTopic(topic) {
    if (!this.connected || !this.stompClient || !this.stompClient.active) {
      return;
    }

    // Check if already subscribed
    if (this.subscriptions.has(topic)) {
      return;
    }

    const subscription = this.stompClient.subscribe(topic, (message) => {
      try {
        const data = JSON.parse(message.body);
        this.lastEventTimestamp = Date.now();
        this.notifyListeners(topic, data);
      } catch (e) {
        console.error('[WebSocket] Failed to parse message:', e);
      }
    });

    this.subscriptions.set(topic, subscription);
    console.log('[WebSocket] Subscribed to:', topic);
  }

  /**
   * Unsubscribe from a STOMP topic.
   */
  unsubscribeFromTopic(topic) {
    const subscription = this.subscriptions.get(topic);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(topic);
      console.log('[WebSocket] Unsubscribed from:', topic);
    }
  }

  /**
   * Add a listener for a specific topic.
   */
  addListener(topic, callback) {
    if (!this.listeners.has(topic)) {
      this.listeners.set(topic, new Set());
      // Subscribe to the topic if connected
      if (this.connected) {
        this.subscribeToTopic(topic);
      }
    }
    this.listeners.get(topic).add(callback);

    // Return unsubscribe function
    return () => {
      this.removeListener(topic, callback);
    };
  }

  /**
   * Remove a listener for a specific topic.
   */
  removeListener(topic, callback) {
    const topicListeners = this.listeners.get(topic);
    if (topicListeners) {
      topicListeners.delete(callback);

      // If no more listeners for this topic, unsubscribe
      if (topicListeners.size === 0) {
        this.unsubscribeFromTopic(topic);
        this.listeners.delete(topic);
      }
    }
  }

  /**
   * Notify all listeners for a topic.
   */
  notifyListeners(topic, data) {
    const topicListeners = this.listeners.get(topic);
    if (topicListeners) {
      topicListeners.forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          console.error('[WebSocket] Error in listener callback:', error);
        }
      });
    }
  }

  /**
   * Add a connection state listener.
   */
  addConnectionListener(callback) {
    this.connectionListeners.push(callback);
    // Immediately notify of current state
    callback(this.connected);
    return () => {
      this.connectionListeners = this.connectionListeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify connection state listeners.
   */
  notifyConnectionListeners(connected) {
    this.connectionListeners.forEach((callback) => {
      try {
        callback(connected);
      } catch (error) {
        console.error('[WebSocket] Error in connection listener:', error);
      }
    });
  }

  // Polling fallback methods

  /**
   * Enable HTTP polling as fallback.
   */
  enablePollingFallback() {
    if (this.pollingEnabled) return;

    console.log('[WebSocket] Enabling polling fallback');
    this.pollingEnabled = true;
    this.startPolling();
  }

  /**
   * Start polling for updates.
   */
  startPolling() {
    if (this.pollingInterval) return;

    this.pollingInterval = setInterval(() => {
      // Components should handle polling via their own fetch logic
      // This is just a notification that polling mode is active
      this.notifyConnectionListeners(false);
    }, this.pollingDelay);
  }

  /**
   * Stop polling.
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * Get org-scoped topic prefix. Falls back to /topic/ if no orgId.
   */
  getOrgTopicPrefix() {
    const orgId = localStorage.getItem('orgId');
    return orgId ? `/topic/${orgId}/` : '/topic/';
  }

  // Convenience methods for synthetic monitoring topics

  /**
   * Subscribe to all execution updates (org-scoped).
   */
  onExecutionUpdate(callback) {
    const orgTopic = `${this.getOrgTopicPrefix()}synthetics/executions`;
    return this.addListener(orgTopic, callback);
  }

  /**
   * Subscribe to updates for a specific execution.
   */
  onExecutionById(executionId, callback) {
    const orgTopic = `${this.getOrgTopicPrefix()}synthetics/executions/${executionId}`;
    return this.addListener(orgTopic, callback);
  }

  /**
   * Subscribe to updates for a specific test.
   */
  onTestExecutions(testId, callback) {
    const orgTopic = `${this.getOrgTopicPrefix()}synthetics/tests/${testId}`;
    return this.addListener(orgTopic, callback);
  }

  /**
   * Subscribe to updates for a specific agent.
   */
  onAgentExecutions(agentId, callback) {
    const orgTopic = `${this.getOrgTopicPrefix()}synthetics/agents/${agentId}`;
    return this.addListener(orgTopic, callback);
  }

  /**
   * Subscribe to general notifications.
   */
  onNotification(callback) {
    const orgTopic = `${this.getOrgTopicPrefix()}synthetics/notifications`;
    return this.addListener(orgTopic, callback);
  }

  /**
   * Check if connected.
   */
  isConnected() {
    return this.connected;
  }

  /**
   * Check if using polling fallback.
   */
  isPolling() {
    return this.pollingEnabled;
  }
}

// Create singleton instance
const syntheticsWebSocketService = new SyntheticsWebSocketService();

export default syntheticsWebSocketService;
